import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Sparkles, Home, Star, Truck } from "lucide-react";
import { BuyBox } from "./BuyBox";

const lineTransition = (i) => ({ duration: 1, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] });

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const imgX = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-10, 10]);
  const glowX = useTransform(sx, [-0.5, 0.5], [22, -22]);
  const glowY = useTransform(sy, [-0.5, 0.5], [14, -14]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24"
    >
      <div className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-champagne/60 blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 -left-52 w-[420px] h-[420px] rounded-full bg-gold-light/30 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 flex flex-col items-center text-center">
        <div className="relative w-full max-w-sm md:max-w-md" data-testid="hero-product-visual">
          <motion.div
            style={{ x: glowX, y: glowY }}
            className="absolute inset-6 rounded-[3rem] bg-gradient-to-br from-champagne via-gold-light/50 to-champagne blur-2xl"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.img
              src="/assets/product-jar.webp"
              alt="ALIVER 24K Gold Hydrogel Eye Masks — 60 patches"
              style={{ x: imgX, y: imgY }}
              className="relative w-full rounded-[3rem] shadow-[0_40px_80px_rgb(168,134,27,0.14)] border border-champagne/70 animate-float-slow"
              data-testid="hero-product-image"
            />
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="absolute -bottom-5 left-6 glass-nav rounded-2xl border border-champagne/70 px-5 py-3 shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
              data-testid="hero-price-badge"
            >
              <span className="font-display text-2xl text-ink">£19.99</span>
              <span className="ml-2 text-xs text-[#8C8C8C]">or £14.99/mo</span>
            </motion.div>
          </motion.div>
        </div>

        <div className="mt-16 max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-xs font-semibold tracking-[0.34em] uppercase text-gold mb-7"
            data-testid="hero-eyebrow"
          >
            24K Gold Hydrogel Eye Masks
          </motion.p>
          <h1 className="font-display tracking-tight text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.02]" data-testid="hero-headline">
            {["Wake Up", "Looking", "Refreshed."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic gold-text" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={lineTransition(i)}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-md mx-auto text-base md:text-lg text-[#5C5C5C] leading-relaxed"
            data-testid="hero-subcopy"
          >
            Cooling hydrogel patches infused with 24K gold, collagen and hyaluronic acid — designed to hydrate and
            refresh the under-eye area as part of an easy at-home ritual.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9"
          >
            <BuyBox idPrefix="hero" align="center" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-medium text-[#5C5C5C]"
            data-testid="hero-trust-indicators"
          >
            <span className="inline-flex items-center gap-1.5"><Sparkles size={14} className="text-gold" strokeWidth={1.5} /> 60 Eye Patches</span>
            <span className="inline-flex items-center gap-1.5"><Truck size={14} className="text-gold" strokeWidth={1.5} /> Free Shipping</span>
            <span className="inline-flex items-center gap-1.5"><Home size={14} className="text-gold" strokeWidth={1.5} /> Easy At-Home Skincare</span>
            <span className="inline-flex items-center gap-1.5"><Star size={14} className="text-gold" strokeWidth={1.5} /> Loved by our community</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
